import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, FolderOpen, Calendar, Users } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import NewProjectModal from "@/components/NewProjectModal";
import { useAuth } from "@/contexts/AuthContext";
import { apiFetch } from "@/lib/apiFetch";

const Projects = () => {
  const { isAuthenticated, logout, loading } = useAuth();
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);

  function getCookie(name: string): string | null {
    const match = document.cookie.match(new RegExp("(^| )" + name + "=([^;]+)"));
    return match ? match[2] : null;
  }

  useEffect(() => {
    // Wait for auth check to complete
    if (loading) return;

    if (!isAuthenticated) {
      navigate("/login");
      return;
    }

    apiFetch("/api/projects")
    .then((projects) => {
      // 'projects' is already the parsed array
      setProjects(projects);
    })
    .catch((err) => {
      console.error("Error fetching projects", err);
    });
  }, [isAuthenticated, loading, navigate]);

  const handleCreateProject = async (newProject: { name: string; description?: string }) => {
    try {
      await fetch("/sanctum/csrf-cookie", { credentials: "include" });
      const headers: HeadersInit = { Accept: "application/json", "Content-Type": "application/json" };
      const xsrfToken = getCookie("XSRF-TOKEN");
      if (xsrfToken) headers["X-XSRF-TOKEN"] = decodeURIComponent(xsrfToken);

      const resp = await fetch("/api/projects", {
        method: "POST",
        credentials: "include",
        headers,
        body: JSON.stringify({
          name: newProject.name,
          description: newProject.description ?? "",
        }),
      });

      if (!resp.ok) {
        const msg = await resp.text();
        throw new Error(msg || resp.statusText);
      }

      const created = await resp.json();

      // Normalize fields your UI expects (members/dueDate are UI-only)
      const project = {
        id: created.id,
        name: created.name,
        description: created.description ?? "",
        members: created.members ?? 1, // or compute from your API if you have it
        dueDate: created.due_date ?? new Date().toISOString().split("T")[0],
        // include any other fields you need from API response
      };

      setProjects((prev) => [...prev, project]);
    } catch (e) {
      console.error("Create project failed:", e);
      // optional: show a toast
      // toast({ title: "Create failed", description: String(e), variant: "destructive" })
    }
  };

  const handleProjectClick = (projectId: number) => {
    navigate(`/dashboard/projects/${projectId}`);
  };

  if (loading) {
    return (
      <div className="text-center py-12">
        <p className="text-muted-foreground">Checking session...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-foreground">Projects</h1>
          <p className="text-muted-foreground">Manage and track your projects</p>
        </div>
        <NewProjectModal onCreateProject={handleCreateProject} />
      </div>

      <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
        {projects.map((project) => (
          <Card
            key={project.id}
            className="shadow-card border border-border bg-gradient-to-br from-card to-card/50 hover:shadow-lg transition-all duration-300 cursor-pointer"
            onClick={() => handleProjectClick(project.id)}
          >
            <CardHeader>
              <div className="flex items-center gap-2">
                <FolderOpen className="h-5 w-5 text-primary" />
                <CardTitle className="text-lg">{project.name}</CardTitle>
              </div>
              <CardDescription className="mt-2">{project.description}</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex items-center justify-between text-sm text-muted-foreground">
                <div className="flex items-center gap-1">
                  <Users className="h-4 w-4" />
                  <span>{project.members || 1} members</span>
                </div>
                <div className="flex items-center gap-1">
                  <Calendar className="h-4 w-4" />
                  <span>{new Date(project.dueDate).toLocaleDateString()}</span>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {projects.length === 0 && (
        <div className="text-center py-12">
          <FolderOpen className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
          <h3 className="text-lg font-medium text-foreground mb-2">No projects yet</h3>
          <p className="text-muted-foreground mb-4">Get started by creating your first project</p>
          <NewProjectModal onCreateProject={handleCreateProject} />
        </div>
      )}
    </div>
  );
};

export default Projects;
